import { Box, Checkbox, Group, Stack, TextInput, Tooltip } from '@mantine/core';
import { getHotkeyHandler } from '@mantine/hooks';
import type { FeatureAccess } from '@repo/api-codegen';
import { Switch } from '@repo/foundations';
import { useState } from 'react';
import { useSuspenseFeatureAccess } from '../../api';
import { GLOBAL_SHORTCUTS } from '../../constants/shortcuts';
import { DefaultDevDropdown } from './DefaultDevDropdown';
import type { DevPanelItemProps } from './DevPanel';

function FeatureFlagSwitch({ flag }: { flag: keyof FeatureAccess }) {
	const { clearOverride, setOverride, originalValues, overrideValues } =
		useSuspenseFeatureAccess();

	const isOverriden = overrideValues[flag] !== undefined;
	const currentValue = isOverriden
		? !!overrideValues[flag]
		: !!originalValues[flag];

	return (
		<Group>
			<Tooltip label={isOverriden ? 'Disable override' : 'Enable override'}>
				<Checkbox
					checked={isOverriden}
					onChange={() =>
						isOverriden ? clearOverride(flag) : setOverride(flag, !currentValue)
					}
				/>
			</Tooltip>
			<Tooltip
				label={`Default value: ${originalValues[flag]}, current value: ${currentValue}`}
			>
				<Box>
					<Switch
						label={flag}
						labelPosition="left"
						disabled={!isOverriden}
						checked={currentValue}
						onChange={(event) => setOverride(flag, event.currentTarget.checked)}
					/>
				</Box>
			</Tooltip>
		</Group>
	);
}

export function FeatureAccess({ close }: DevPanelItemProps) {
	const [search, setSearch] = useState('');
	const { originalValues, overrideValues } = useSuspenseFeatureAccess();

	const filteredFlags = Object.keys(originalValues)
		.filter((flag) => flag.toLowerCase().includes(search.toLowerCase()))
		.sort((a, b) => {
			const isOverridenA =
				overrideValues[a as keyof FeatureAccess] !== undefined;
			const isOverridenB =
				overrideValues[b as keyof FeatureAccess] !== undefined;
			if (isOverridenA && !isOverridenB) {
				return -1;
			}
			if (!isOverridenA && isOverridenB) {
				return 1;
			}

			return 0;
		});

	return (
		<DefaultDevDropdown label="Feature Access" close={close}>
			<Stack pr="md" style={{ maxHeight: '500px', overflowY: 'auto' }}>
				<TextInput
					placeholder="Search feature access"
					value={search}
					onChange={(event) => setSearch(event.currentTarget.value)}
					autoFocus
					onKeyDown={getHotkeyHandler([[GLOBAL_SHORTCUTS.ESCAPE[0], close]])}
				/>
				{filteredFlags.map((flag) => (
					<FeatureFlagSwitch key={flag} flag={flag as keyof FeatureAccess} />
				))}
			</Stack>
		</DefaultDevDropdown>
	);
}
